/**
 * Truthful QA Browser readiness derivation for the Browser Profiles dashboard.
 *
 * Sources (no fabricated values):
 * - providerConfigured: authorizedQaProxy.credentialSaved / providerCredentialsConfigured
 * - locationConfigured: identity_profile | manual_override | targeted filters beyond defaults
 * - connectionPassed: lastConnectionTestStatus === 'passed' with a launch-eligible location
 * - localSetupState: what THIS browser origin was told about local Install/Update.
 *   Disk presence of ~/.fundelite-camoufox-venv + the launcher cannot be observed from the
 *   browser, and the acknowledgment lives in origin-scoped localStorage (app.fundelite.ai and
 *   app.fundelitehq.com never share it). A missing acknowledgment therefore means "unknown",
 *   never "not installed" — the launch command itself checks the local machine.
 * - sessionActive: always false — no reliable live Camoufox session signal exists
 */

import {
  QA_LOCATION_SOURCE_DEFAULT,
  QA_LOCATION_SOURCE_IDENTITY,
  QA_LOCATION_SOURCE_MANUAL,
  QA_TARGETING_ALL_ASNS,
  QA_TARGETING_ALL_CITIES,
  QA_TARGETING_ALL_STATES,
  QA_TARGETING_ALL_ZIPS,
  QA_TARGETING_MODE_FILTERS,
  PROXY_BADGE_AUTHORIZED,
  QA_LAUNCHER_MIN_COMPATIBLE_VERSION,
  QA_LAUNCHER_TEMPLATE_VERSION,
  normalizeAuthorizedQaProxySummary,
} from './fundingAuthorizedQaProxyService';

/** Bump when QA launcher template / bootstrap contract meaningfully changes.
 * v3: full_web CONNECT tunnel hardenings (raw 200 + parse-error guards).
 * v4: full_web skips page host routes; operator wait pumps Playwright so
 *     Firefox chrome navigations are not frozen behind route.continue_().
 * v5: geo match fail-closed for state mismatch; preflight location gate;
 *     verified timezone/locale at Camoufox construction; truthful geo errors.
 * v6: QA sessions load without Camoufox's bundled uBlock Origin so redirect/cookie
 *     handshakes complete; saved-profile cookies are never cleared on shutdown.
 * v7: launcher carries an on-disk version marker checked by the Launch QA Session
 *     command; request cancellations no longer replace the page with a proxy error;
 *     host-only document redirect diagnostics.
 * v8: the session stays open until the operator presses Enter, closes every browser
 *     window, or interrupts the launcher; stdin EOF and typed-ahead input no longer end it.
 * v9: the gateway keeps the provider's CONNECT status and classifies tunnel failures
 *     (e.g. a provider-blocked site shows as such, not as a generic bad gateway); a failed
 *     iframe navigation no longer replaces the top-level page with the proxy error page.
 * v10: the gateway parses bracketed IPv6 CONNECT targets and blocks private IPv6,
 *     malformed and single-label hosts locally instead of forwarding them upstream.
 * v11: the launch's upstream provider (DataImpulse or 911Proxy) labels gateway logs and
 *     error pages; an expired provider endpoint (HTTP 509 "port expire") fails closed
 *     with a "Generate a new QA session" message instead of a generic tunnel error.
 * v12: 911Proxy sessions keep their single upstream endpoint warm with a tiny public-IP
 *     check every 30s (no second fetch, no rotation); a 509 during the session marks it
 *     expired and the gateway refuses further traffic with "Launch a new QA session".
 * v13: per-tunnel diagnostics (stage, timings, byte counts, end reason; never endpoints or
 *     payloads); repeated non-509 keepalive failures mark the session degraded instead of
 *     stopping the keepalive, and the next same-endpoint success marks it recovered.
 * v14: manual operator mode — after verification, keepalive start and the single startup
 *     page, the launcher only observes (the example.com full-web navigation probe is gone).
 * v15: after exact 911Proxy exit verification the launcher reports session events
 *     (verified, keepalive, degraded/recovered, expired, closed) to the admin IP Usage
 *     registry; best-effort and direct to FundElite, routing and keepalive unchanged.
 * v16: terminal input (Enter, typed text, a pasted launch command) no longer ends the
 *     session — it is discarded; the session ends on closing the browser or Ctrl+C. Expired
 *     and failed-verification sessions print "open a new Terminal tab" relaunch steps.
 * v17: 911Proxy browser exit verification races the three IP hosts in parallel on fresh
 *     connections (25s per round, at most 2 rounds) because the provider can delay the first
 *     byte ~20s; exact match and fail-closed unchanged; DataImpulse verification unchanged.
 * v18: one QA Browser runtime per local QA profile. An expired 911Proxy session turns every
 *     tab into a static recovery page and is closed when a fresh session starts; a launch
 *     while a healthy session runs is refused before its token is redeemed. Session restore
 *     stays off, so expired tabs never reopen. Proxy, verification and keepalive unchanged.
 * v19: DataImpulse initial preflight rotates its sticky session id after a location mismatch,
 *     so each of the 3 bounded retries genuinely re-selects a residential exit instead of
 *     re-checking the same session; the exact verified upstream is reused for the browser.
 *     911Proxy, verification, and all other behavior unchanged.
 * v20: QA protocol-guard install/self-test diagnostics. Adds sanitized structured stages for
 *     add-on install and for each self-test probe (classified outcome + fixed error token),
 *     and a final failure reason. Fail-closed behavior, security policy and timeout unchanged.
 * v21: QA Session Monitor. Server-authorized session TTL (DataImpulse <=60m, 911Proxy <=90m)
 *     enforced by a local monotonic deadline; a read-only localhost status bridge + shared
 *     session-monitor extension; 30s exit-IP health check with fail-closed revalidation.
 *     Routing, providers, protocol guard, and verification unchanged.
 * v22: DataImpulse reports its accepted browser exit to the admin IP Usage registry (shared
 *     with 911Proxy). 911Proxy still requires an exact endpoint match; DataImpulse reports its
 *     location-compatible exit. Routing, verification, TTL and all other behavior unchanged.
 * v23: 911Proxy endpoint `life` now follows the authorized session duration (clamped to the
 *     provider range). Provider-endpoint expiry is reported as "Proxy Endpoint Ended Early"
 *     and kept distinct from a FundElite session-TTL expiry; no silent replacement of the exit
 *     IP. The session-monitor extension gains a runtime-verified startup handshake, a visible
 *     toolbar icon, and background announce. Control renamed "Maximum Authorized QA Time".
 */
export const QA_BROWSER_LAUNCHER_TEMPLATE_VERSION = QA_LAUNCHER_TEMPLATE_VERSION;

/** Oldest launcher that may still run sessions; shared with the Launch QA Session file check. */
export const QA_BROWSER_LAUNCHER_MIN_COMPATIBLE_VERSION = QA_LAUNCHER_MIN_COMPATIBLE_VERSION;

export const QA_LOCAL_SETUP_STATE = Object.freeze({
  CURRENT: 'current',
  OUTDATED: 'outdated',
  UNKNOWN: 'unknown',
  REQUIRED: 'required',
});

export const QA_PRIMARY_ACTION = Object.freeze({
  SETUP: 'setup',
  SET_LOCATION: 'set_location',
  CHOOSE_NEARBY: 'choose_nearby',
  TEST_CONNECTION: 'test_connection',
  INSTALL_UPDATE: 'install_update',
  LAUNCH: 'launch',
  TERMINATE_TEST: 'terminate_test',
  SUSPENDED: 'suspended',
  NOT_ENTITLED: 'not_entitled',
});

export const QA_PRIMARY_ACTION_LABELS = Object.freeze({
  [QA_PRIMARY_ACTION.SETUP]: 'Set up QA Browser',
  [QA_PRIMARY_ACTION.SET_LOCATION]: 'Set QA Test Location',
  [QA_PRIMARY_ACTION.CHOOSE_NEARBY]: 'Choose nearby QA location',
  [QA_PRIMARY_ACTION.TEST_CONNECTION]: 'Test Proxy Connection',
  [QA_PRIMARY_ACTION.INSTALL_UPDATE]: 'Install / Update QA Browser',
  [QA_PRIMARY_ACTION.LAUNCH]: 'Launch QA Session',
  [QA_PRIMARY_ACTION.TERMINATE_TEST]: 'Terminate Test',
  [QA_PRIMARY_ACTION.SUSPENDED]: 'Resume proxy',
  [QA_PRIMARY_ACTION.NOT_ENTITLED]: 'Proxy add-on required',
});

// Keyed by client + profile only: the launcher file is per client/profile, and the agency id
// can resolve from different sources (client record vs workspace prop) between visits.
function storageKey({ clientId, profileId }) {
  return `fundelite.qaBrowser.launcherAck.${clientId || 'na'}.${profileId || 'na'}`;
}

function legacyStorageKey({ agencyId, clientId, profileId }) {
  return `fundelite.qaBrowser.launcherAck.${agencyId || 'na'}.${clientId || 'na'}.${profileId || 'na'}`;
}

// Used only when localStorage is unavailable or throws (e.g. Safari private mode), so an
// acknowledgment still survives re-renders and re-fetches for this page session.
const memoryAcknowledgments = new Map();

function resolveStore(storage) {
  if (storage) return storage;
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function parseAcknowledgment(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.templateVersion) return null;
    return {
      templateVersion: String(parsed.templateVersion),
      acknowledgedAt: parsed.acknowledgedAt || null,
    };
  } catch {
    return null;
  }
}

export function readQaLauncherAcknowledgment({ agencyId, clientId, profileId, storage } = {}) {
  const store = resolveStore(storage);
  const key = storageKey({ clientId, profileId });
  if (store) {
    try {
      return parseAcknowledgment(store.getItem(key))
        || parseAcknowledgment(store.getItem(legacyStorageKey({ agencyId, clientId, profileId })))
        || parseAcknowledgment(memoryAcknowledgments.get(key));
    } catch {
      // storage unreadable: fall through to the in-memory copy
    }
  }
  return parseAcknowledgment(memoryAcknowledgments.get(key));
}

export function acknowledgeQaLauncherCurrent({
  clientId,
  profileId,
  templateVersion = QA_BROWSER_LAUNCHER_TEMPLATE_VERSION,
  storage,
  now = Date.now(),
} = {}) {
  const payload = {
    templateVersion: String(templateVersion || QA_BROWSER_LAUNCHER_TEMPLATE_VERSION),
    acknowledgedAt: new Date(now).toISOString(),
  };
  const key = storageKey({ clientId, profileId });
  const serialized = JSON.stringify(payload);
  const store = resolveStore(storage);
  let persisted = true;
  try {
    if (!store) throw new Error('storage unavailable');
    store.setItem(key, serialized);
    memoryAcknowledgments.delete(key);
  } catch {
    // Storage blocked or full: keep the acknowledgment for this page session.
    memoryAcknowledgments.set(key, serialized);
    persisted = false;
  }
  return { ...payload, persisted };
}

export function clearQaLauncherAcknowledgment({ agencyId, clientId, profileId, storage } = {}) {
  const key = storageKey({ clientId, profileId });
  memoryAcknowledgments.delete(key);
  const store = resolveStore(storage);
  try {
    store?.removeItem(key);
    store?.removeItem(legacyStorageKey({ agencyId, clientId, profileId }));
  } catch {
    // ignore
  }
}

export function isProviderConfigured(proxy) {
  const normalized = normalizeAuthorizedQaProxySummary(proxy) || proxy || {};
  return Boolean(
    normalized.providerCredentialsConfigured
    || normalized.credentialsConfigured
    || normalized.credentialSaved,
  );
}

export function isLocationConfigured(proxy) {
  const normalized = normalizeAuthorizedQaProxySummary(proxy) || proxy || {};
  if (normalized.providerTargetReady === false
    || String(normalized.providerTargetStatus || '').toLowerCase() === 'selection_required') {
    return false;
  }
  const source = String(
    normalized.qaLocationSource || normalized.locationSource || QA_LOCATION_SOURCE_DEFAULT,
  ).trim().toLowerCase();
  if (source === QA_LOCATION_SOURCE_IDENTITY || source === QA_LOCATION_SOURCE_MANUAL) {
    return true;
  }
  const targeting = normalized.targeting || {};
  if (targeting.mode === QA_TARGETING_MODE_FILTERS || targeting.usesTargetFilters) {
    const hasState = targeting.state && targeting.state !== QA_TARGETING_ALL_STATES;
    const hasCity = targeting.city && targeting.city !== QA_TARGETING_ALL_CITIES;
    const hasZip = targeting.zip && targeting.zip !== QA_TARGETING_ALL_ZIPS;
    const hasAsn = targeting.asn && targeting.asn !== QA_TARGETING_ALL_ASNS;
    return Boolean(hasState || hasCity || hasZip || hasAsn);
  }
  return false;
}

export function targetingFingerprint(targeting = {}) {
  return [
    targeting.mode || '',
    targeting.country || '',
    targeting.state || '',
    targeting.city || '',
    targeting.zip || '',
    targeting.asn || '',
  ].join('|');
}

export function isLauncherCurrent({
  agencyId,
  clientId,
  profileId,
  templateVersion = QA_BROWSER_LAUNCHER_TEMPLATE_VERSION,
  storage,
} = {}) {
  const ack = readQaLauncherAcknowledgment({ agencyId, clientId, profileId, storage });
  if (!ack?.templateVersion) return false;
  return ack.templateVersion === String(templateVersion || QA_BROWSER_LAUNCHER_TEMPLATE_VERSION);
}

function launcherVersionNumber(version) {
  const match = /-v(\d+)$/.exec(String(version || '').trim());
  return match ? Number(match[1]) : null;
}

/** Local launcher knowledge for this origin: current | outdated | required | unknown. */
export function deriveQaLocalSetupState({
  acknowledgedVersion,
  templateVersion = QA_BROWSER_LAUNCHER_TEMPLATE_VERSION,
  minCompatibleVersion = QA_BROWSER_LAUNCHER_MIN_COMPATIBLE_VERSION,
} = {}) {
  const acked = String(acknowledgedVersion || '').trim();
  if (!acked) return QA_LOCAL_SETUP_STATE.UNKNOWN;
  if (acked === String(templateVersion)) return QA_LOCAL_SETUP_STATE.CURRENT;
  const ackedNumber = launcherVersionNumber(acked);
  const minNumber = launcherVersionNumber(minCompatibleVersion);
  if (ackedNumber !== null && minNumber !== null && ackedNumber < minNumber) {
    return QA_LOCAL_SETUP_STATE.REQUIRED;
  }
  return QA_LOCAL_SETUP_STATE.OUTDATED;
}

export const QA_NEXT_ACTION_TERMINATE_TEST = 'terminate_test';

export const QA_CONNECTION_PHASE = Object.freeze({
  NEVER_TESTED: 'never_tested',
  TESTING: 'testing',
  STOPPING: 'stopping',
  TERMINATED: 'terminated',
  NETWORK_ONLY: 'network_only',
  STATE_COMPATIBLE: 'state_compatible',
  CITY_COMPATIBLE: 'city_compatible',
  COMPATIBLE: 'compatible',
  MATCHED: 'matched',
  MISMATCH: 'mismatch',
  ERROR: 'error',
});

export const QA_LAUNCH_BLOCKED_REASON = Object.freeze({
  NOT_ENTITLED: 'not_entitled',
  SUSPENDED: 'suspended',
  PROVIDER_NOT_CONFIGURED: 'provider_not_configured',
  TARGET_NOT_READY: 'target_not_ready',
  TEST_IN_PROGRESS: 'test_in_progress',
  TEST_TERMINATED: 'test_terminated',
  NEVER_TESTED: 'never_tested',
  STALE_PROOF: 'stale_proof',
  LOCATION_NOT_EXACT: 'location_not_exact',
  LOCATION_MISMATCH: 'location_mismatch',
  CONNECTION_FAILED: 'connection_failed',
  LAUNCHER_NOT_GENERATED: 'launcher_not_generated',
  LAUNCHER_UPDATE_REQUIRED: 'launcher_update_required',
  PROXY_NOT_ACTIVE: 'proxy_not_active',
});

function proofFingerprintOf(proof) {
  return proof?.targetFingerprint || proof?.target?.fingerprint || null;
}

/** The backend's fingerprint for the active target; local computation only for older APIs. */
export function activeTargetFingerprintFor(proxy) {
  const normalized = normalizeAuthorizedQaProxySummary(proxy) || proxy || {};
  return normalized.activeTargetFingerprint || targetingFingerprint(normalized.targeting || {});
}

/** A proof without a fingerprint predates target binding; the backend gate accepts it too. */
export function isProofForActiveTarget(proof, proxy) {
  const fingerprint = proofFingerprintOf(proof);
  if (!fingerprint) return true;
  return fingerprint === activeTargetFingerprintFor(proxy);
}

/**
 * Newest connection proof for the active target between this page's last test response and
 * the persisted summary. A proof for a previous target never outranks one for the current
 * target. Persisted proofs carry their status on the summary, not the proof.
 */
export function selectLatestConnectionProof(sessionProof, proxy) {
  const normalized = normalizeAuthorizedQaProxySummary(proxy) || proxy || {};
  const storedRaw = normalized.lastConnectionTestProof || null;
  const stored = storedRaw
    ? { ...storedRaw, status: storedRaw.status || normalized.lastConnectionTestStatus || null }
    : null;
  if (!sessionProof) return stored;
  if (!stored) return sessionProof;
  const sessionCurrent = isProofForActiveTarget(sessionProof, normalized);
  const storedCurrent = isProofForActiveTarget(stored, normalized);
  if (sessionCurrent !== storedCurrent) return sessionCurrent ? sessionProof : stored;
  const sessionAt = Date.parse(sessionProof.testedAt || '');
  const storedAt = Date.parse(stored.testedAt || '');
  if (!Number.isNaN(storedAt) && (Number.isNaN(sessionAt) || storedAt > sessionAt)) {
    return stored;
  }
  return sessionProof;
}

function requestedPrecisionFor(targeting, verification) {
  if (verification?.requestedPrecision) return String(verification.requestedPrecision).toLowerCase();
  if (targeting?.zip && targeting.zip !== QA_TARGETING_ALL_ZIPS) return 'zip';
  if (targeting?.city && targeting.city !== QA_TARGETING_ALL_CITIES) return 'city';
  return 'state';
}

/** Connection facts from one proof, independent of local install state and test phase. */
function evaluateConnectionProof(normalized, proof) {
  const targeting = normalized.targeting || {};
  const verification = proof?.locationVerification || {};
  const precision = requestedPrecisionFor(targeting, verification);
  const providerTargetReady = normalized.providerTargetReady !== false
    && String(normalized.providerTargetStatus || '').toLowerCase() !== 'selection_required';
  if (!proof) {
    return {
      precision,
      providerTargetReady,
      proofCurrent: true,
      outcome: normalized.lastConnectionTestStatus ? QA_CONNECTION_PHASE.ERROR : QA_CONNECTION_PHASE.NEVER_TESTED,
      status: normalized.lastConnectionTestStatus || null,
      locationStatus: null,
      networkConfirmed: false,
      sufficient: false,
    };
  }
  const proofCurrent = isProofForActiveTarget(proof, normalized);
  const status = String(proof.status || normalized.lastConnectionTestStatus || '').trim().toLowerCase();
  const locationStatus = String(proof.locationStatus || verification.locationStatus || '').trim().toLowerCase();
  const networkConfirmed = proof.differentIp === true
    || String(proof.networkStatus || '').toLowerCase() === 'confirmed'
    || status === 'passed';
  let outcome;
  if (!proofCurrent) {
    outcome = QA_CONNECTION_PHASE.NEVER_TESTED;
  } else if (locationStatus === 'mismatch') {
    outcome = QA_CONNECTION_PHASE.MISMATCH;
  } else if (locationStatus === 'state_compatible') {
    outcome = QA_CONNECTION_PHASE.STATE_COMPATIBLE;
  } else if (locationStatus === 'city_compatible') {
    outcome = QA_CONNECTION_PHASE.CITY_COMPATIBLE;
  } else if (status !== 'passed') {
    outcome = networkConfirmed ? QA_CONNECTION_PHASE.NETWORK_ONLY : QA_CONNECTION_PHASE.ERROR;
  } else if (locationStatus === 'unverified' || locationStatus === 'unavailable'
    || verification.launchEligible === false) {
    outcome = QA_CONNECTION_PHASE.NETWORK_ONLY;
  } else if (locationStatus === 'compatible') {
    outcome = QA_CONNECTION_PHASE.COMPATIBLE;
  } else {
    // Passed with an exact match (or a pre-verification proof the backend gate also accepts).
    outcome = QA_CONNECTION_PHASE.MATCHED;
  }
  // Exact ZIP/city targets require matched; only state/country precision accepts compatible.
  const sufficient = Boolean(
    providerTargetReady
    && proofCurrent
    && (outcome === QA_CONNECTION_PHASE.MATCHED
      || (outcome === QA_CONNECTION_PHASE.COMPATIBLE && precision !== 'zip' && precision !== 'city')),
  );
  return {
    precision,
    providerTargetReady,
    proofCurrent,
    outcome,
    status: status || null,
    locationStatus: locationStatus || null,
    networkConfirmed,
    sufficient,
  };
}

export function isConnectionPassed(proxy, { liveProof } = {}) {
  const normalized = normalizeAuthorizedQaProxySummary(proxy) || proxy || {};
  return evaluateConnectionProof(normalized, liveProof || normalized.lastConnectionTestProof || null)
    .sufficient;
}

const CONNECTION_STEP_STATUS = Object.freeze({
  [QA_CONNECTION_PHASE.TESTING]: 'Testing…',
  [QA_CONNECTION_PHASE.STOPPING]: 'Stopping…',
  [QA_CONNECTION_PHASE.TERMINATED]: 'Test stopped',
  [QA_CONNECTION_PHASE.NEVER_TESTED]: 'Not tested',
  [QA_CONNECTION_PHASE.MATCHED]: 'Verified',
  [QA_CONNECTION_PHASE.COMPATIBLE]: 'Verified',
  [QA_CONNECTION_PHASE.STATE_COMPATIBLE]: 'Needs location verification',
  [QA_CONNECTION_PHASE.CITY_COMPATIBLE]: 'Needs ZIP verification',
  [QA_CONNECTION_PHASE.NETWORK_ONLY]: 'Needs location verification',
  [QA_CONNECTION_PHASE.MISMATCH]: 'Location mismatch',
  [QA_CONNECTION_PHASE.ERROR]: 'Failed',
});

const INSTALLED_SETUP_STATES = [QA_LOCAL_SETUP_STATE.CURRENT, QA_LOCAL_SETUP_STATE.OUTDATED];

function deriveLauncherStep({ hasQaProxySession, setupState }) {
  if (!hasQaProxySession) {
    return { status: 'Next', detail: 'Install the QA Browser on this computer' };
  }
  if (setupState === QA_LOCAL_SETUP_STATE.REQUIRED) {
    return { status: 'Update required', detail: 'The installed launcher is too old to run QA sessions' };
  }
  if (setupState === QA_LOCAL_SETUP_STATE.OUTDATED) {
    return { status: 'Update available', detail: 'Installed · Update available' };
  }
  if (setupState === QA_LOCAL_SETUP_STATE.CURRENT) {
    return { status: 'Installed', detail: 'Installed on this computer' };
  }
  return { status: 'Ready to launch', detail: 'Local setup not confirmed in this browser' };
}

function deriveNextStep({ primaryAction, installed, connectionVerified, updateRequired }) {
  switch (primaryAction) {
    case QA_PRIMARY_ACTION.SETUP:
      return {
        title: 'Set up QA Browser',
        body: 'Configure Authorized QA Proxy so this client can run proxied QA sessions.',
      };
    case QA_PRIMARY_ACTION.CHOOSE_NEARBY:
      return {
        title: 'Choose nearby QA location',
        body: 'The requested Personal Address ZIP is not currently available from DataImpulse. Pick a provider-supported nearby ZIP to continue.',
      };
    case QA_PRIMARY_ACTION.SET_LOCATION:
      return {
        title: 'Set QA Test Location',
        body: 'Choose where QA traffic should appear from before testing the connection.',
      };
    case QA_PRIMARY_ACTION.INSTALL_UPDATE:
      return {
        title: 'Install QA Browser',
        body: 'Provider and QA location are ready. Install the QA Browser on this computer, then launch a QA session.',
      };
    case QA_PRIMARY_ACTION.LAUNCH:
      if (updateRequired) {
        return {
          title: 'Update QA Browser',
          body: 'The QA Browser installed on this computer is too old to run QA sessions. Run Install / Update, then launch.',
        };
      }
      return {
        title: 'Launch QA Browser',
        body: connectionVerified
          ? 'The QA location is verified. Launch QA Session issues a short-lived command for the QA Browser already installed on this computer.'
          : `${installed ? 'QA Browser is installed. ' : ''}Launch QA Session issues a short-lived command; traffic always routes through the QA proxy. The exact QA location is not verified yet — see the connection check below.`,
      };
    case QA_PRIMARY_ACTION.SUSPENDED:
      return {
        title: 'Resume proxy',
        body: 'Authorized QA Proxy is suspended for this client. Resume it to continue.',
      };
    case QA_PRIMARY_ACTION.NOT_ENTITLED:
      return {
        title: 'Proxy add-on required',
        body: 'This client does not have the Authorized QA Proxy add-on enabled.',
      };
    default:
      return { title: 'Continue setup', body: 'Complete the remaining QA Browser steps.' };
  }
}

/**
 * The single authority for the QA Browser card. Install, connection verification and launch
 * eligibility are independent inputs.
 *
 * Primary-action precedence: not entitled → suspended → provider setup → location selection →
 * first launcher install → Launch QA Session (permanent once a launcher exists, even when the
 * launch is currently blocked; the click explains launch.blockedReason).
 *
 * @param {object} inputs
 * @param {object} inputs.proxy authorizedQaProxy summary
 * @param {{templateVersion: string}|null} inputs.acknowledgment local setup acknowledgment
 * @param {boolean} inputs.hasQaProxySession a launcher has been generated for this profile
 * @param {object|null} inputs.proof newest connection proof (see selectLatestConnectionProof)
 * @param {string} inputs.testPhase '' | 'running' | 'stopping' | 'stopped'
 * @param {string} [inputs.lastTestedLabel] formatted time of the last test, for display only
 */
export function deriveQaBrowserState({
  proxy,
  acknowledgment = null,
  hasQaProxySession = false,
  proof = null,
  testPhase = '',
  lastTestedLabel = '',
  templateVersion = QA_BROWSER_LAUNCHER_TEMPLATE_VERSION,
  minCompatibleVersion = QA_BROWSER_LAUNCHER_MIN_COMPATIBLE_VERSION,
} = {}) {
  const normalized = normalizeAuthorizedQaProxySummary(proxy) || proxy || {};
  const entitled = normalized.entitled !== false;
  const proxyStatus = String(normalized.status || '').toLowerCase();
  const suspended = proxyStatus === 'suspended';
  const providerConfigured = isProviderConfigured(normalized);
  const locationConfigured = isLocationConfigured(normalized);
  const proxyActive = proxyStatus === 'active' || normalized.badge === PROXY_BADGE_AUTHORIZED;

  const evaluated = evaluateConnectionProof(normalized, proof);
  const selectionRequired = !evaluated.providerTargetReady
    || Boolean(normalized.locationSelectionRequired?.required);

  const runningPhase = String(testPhase || '').toLowerCase();
  let phase = evaluated.outcome;
  if (runningPhase === 'running') phase = QA_CONNECTION_PHASE.TESTING;
  else if (runningPhase === 'stopping') phase = QA_CONNECTION_PHASE.STOPPING;
  else if (runningPhase === 'stopped') phase = QA_CONNECTION_PHASE.TERMINATED;
  const testActive = phase === QA_CONNECTION_PHASE.TESTING || phase === QA_CONNECTION_PHASE.STOPPING;
  // Stopping a re-test saves nothing, so an earlier proof for this target keeps its verdict.
  const connectionVerified = evaluated.sufficient && !testActive;

  const acknowledgedVersion = acknowledgment?.templateVersion || null;
  const setupState = hasQaProxySession
    ? deriveQaLocalSetupState({ acknowledgedVersion, templateVersion, minCompatibleVersion })
    : QA_LOCAL_SETUP_STATE.REQUIRED;
  let installed = null;
  if (!hasQaProxySession) installed = false;
  else if (acknowledgedVersion) installed = true;
  const browser = {
    installed,
    setupState,
    acknowledged: Boolean(acknowledgedVersion),
    version: acknowledgedVersion,
    templateVersion,
    minimumCompatibleVersion: minCompatibleVersion,
    // Unknown local setup never blocks: the launch command verifies the machine itself.
    compatible: hasQaProxySession && setupState !== QA_LOCAL_SETUP_STATE.REQUIRED,
    updateAvailable: setupState === QA_LOCAL_SETUP_STATE.OUTDATED,
    updateRequired: setupState === QA_LOCAL_SETUP_STATE.REQUIRED,
  };

  // Connection verification is diagnostic: it never gates launch or hides Launch QA Session.
  // The backend issues tokens once provider + active target + active assignment exist, and the
  // launcher itself fails closed on proxy/gateway errors.
  const targetReady = evaluated.providerTargetReady && locationConfigured;
  let blockedReason = null;
  if (!entitled) blockedReason = QA_LAUNCH_BLOCKED_REASON.NOT_ENTITLED;
  else if (suspended) blockedReason = QA_LAUNCH_BLOCKED_REASON.SUSPENDED;
  else if (!providerConfigured) blockedReason = QA_LAUNCH_BLOCKED_REASON.PROVIDER_NOT_CONFIGURED;
  else if (!targetReady) blockedReason = QA_LAUNCH_BLOCKED_REASON.TARGET_NOT_READY;
  else if (!hasQaProxySession) blockedReason = QA_LAUNCH_BLOCKED_REASON.LAUNCHER_NOT_GENERATED;
  else if (browser.updateRequired) blockedReason = QA_LAUNCH_BLOCKED_REASON.LAUNCHER_UPDATE_REQUIRED;
  else if (!proxyActive) blockedReason = QA_LAUNCH_BLOCKED_REASON.PROXY_NOT_ACTIVE;
  const eligible = blockedReason === null;

  // Once a QA launcher exists, Launch QA Session is the permanent primary action. Testing,
  // terminating a test and changing location live in the connection proof card.
  let primaryAction;
  if (!entitled) primaryAction = QA_PRIMARY_ACTION.NOT_ENTITLED;
  else if (suspended) primaryAction = QA_PRIMARY_ACTION.SUSPENDED;
  else if (!providerConfigured) primaryAction = QA_PRIMARY_ACTION.SETUP;
  else if (!targetReady) {
    primaryAction = selectionRequired ? QA_PRIMARY_ACTION.CHOOSE_NEARBY : QA_PRIMARY_ACTION.SET_LOCATION;
  } else if (!hasQaProxySession) primaryAction = QA_PRIMARY_ACTION.INSTALL_UPDATE;
  else primaryAction = QA_PRIMARY_ACTION.LAUNCH;

  let primaryActionLabel = QA_PRIMARY_ACTION_LABELS[primaryAction];
  if (primaryAction === QA_PRIMARY_ACTION.SETUP && providerConfigured) {
    primaryActionLabel = 'QA Settings';
  }

  let statusLabel;
  if (!entitled) statusLabel = 'Add-on required';
  else if (suspended) statusLabel = 'Suspended';
  else if (primaryAction === QA_PRIMARY_ACTION.INSTALL_UPDATE) statusLabel = 'Install required';
  else if (browser.updateRequired) statusLabel = 'Update required';
  else if (eligible && setupState === QA_LOCAL_SETUP_STATE.CURRENT) statusLabel = 'QA Browser Ready';
  else if (eligible && setupState === QA_LOCAL_SETUP_STATE.OUTDATED) statusLabel = 'Update available';
  else if (eligible) statusLabel = 'Ready to launch';
  else if (providerConfigured && targetReady) statusLabel = 'Proxy not active';
  else if (providerConfigured) statusLabel = 'Location selection required';
  else statusLabel = 'Setup required';

  const labelPhase = phase === QA_CONNECTION_PHASE.TERMINATED && proof && evaluated.proofCurrent
    ? evaluated.outcome
    : phase;
  let connectionStatusLabel = CONNECTION_STEP_STATUS[labelPhase] || 'Retest needed';
  if (!testActive && !evaluated.providerTargetReady) connectionStatusLabel = 'Waiting';
  else if (labelPhase === QA_CONNECTION_PHASE.MISMATCH && proof?.locationVerification?.mismatchLevel === 'zip') {
    connectionStatusLabel = 'ZIP mismatch';
  } else if (labelPhase === QA_CONNECTION_PHASE.COMPATIBLE && !evaluated.sufficient) {
    connectionStatusLabel = 'Needs ZIP verification';
  }
  let connectionDetail;
  if (phase === QA_CONNECTION_PHASE.TESTING) connectionDetail = 'Testing proxy connection';
  else if (phase === QA_CONNECTION_PHASE.STOPPING) connectionDetail = 'Stopping test';
  else if (!evaluated.providerTargetReady) connectionDetail = 'Waiting for QA location';
  else if (!evaluated.proofCurrent) connectionDetail = 'QA location changed since the last test';
  else connectionDetail = lastTestedLabel || (evaluated.sufficient ? 'Verified' : 'Not tested');

  const launcherStep = deriveLauncherStep({ hasQaProxySession, setupState });
  const installedLocally = INSTALLED_SETUP_STATES.includes(setupState) && hasQaProxySession;

  return {
    provider: { configured: providerConfigured, ready: providerConfigured, entitled, suspended },
    location: {
      configured: locationConfigured,
      providerTargetReady: evaluated.providerTargetReady,
      selectionRequired,
      precision: evaluated.precision,
      verificationStatus: evaluated.locationStatus,
    },
    connection: {
      phase,
      networkConfirmed: evaluated.networkConfirmed,
      locationVerified: evaluated.sufficient,
      verified: connectionVerified,
      proofCurrent: evaluated.proofCurrent,
      lastResult: evaluated.status,
    },
    browser,
    launch: { eligible, blockedReason },
    primaryAction,
    primaryActionLabel,
    statusLabel,
    nextStep: deriveNextStep({
      primaryAction,
      installed: installedLocally,
      connectionVerified,
      updateRequired: browser.updateRequired,
    }),
    steps: {
      provider: { status: providerConfigured ? 'Ready' : 'Pending', done: providerConfigured },
      location: {
        status: locationConfigured ? 'Ready' : (!evaluated.providerTargetReady ? 'Selection required' : 'Next'),
        done: locationConfigured,
      },
      connection: { status: connectionStatusLabel, detail: connectionDetail, done: connectionVerified },
      launcher: { ...launcherStep, done: eligible },
    },
    secondary: {
      installUpdate: hasQaProxySession && entitled && primaryAction === QA_PRIMARY_ACTION.LAUNCH,
    },
    testActive,
    diagnostics: {
      providerReady: providerConfigured,
      providerTargetReady: evaluated.providerTargetReady,
      activeTargetFingerprint: activeTargetFingerprintFor(normalized),
      proofTargetFingerprint: proofFingerprintOf(proof),
      proofCurrent: proof ? evaluated.proofCurrent : null,
      connectionPhase: phase,
      connectionStatus: evaluated.status,
      locationStatus: evaluated.locationStatus,
      setupAcknowledged: Boolean(acknowledgedVersion),
      launcherVersion: acknowledgedVersion,
      launcherCompatible: browser.compatible,
      launchEligible: eligible,
      blockedReason,
      primaryAction,
    },
  };
}

/** Back-compat view of deriveQaBrowserState plus the local acknowledgment read. */
export function deriveQaBrowserReadiness({
  proxy,
  agencyId,
  clientId,
  profileId,
  hasQaProxySession = false,
  templateVersion = QA_BROWSER_LAUNCHER_TEMPLATE_VERSION,
  storage,
  liveProof,
  connectionTestPhase = null,
  lastTestedLabel = '',
} = {}) {
  const normalized = normalizeAuthorizedQaProxySummary(proxy) || proxy || {};
  const acknowledgment = readQaLauncherAcknowledgment({ agencyId, clientId, profileId, storage });
  const state = deriveQaBrowserState({
    proxy: normalized,
    acknowledgment,
    hasQaProxySession,
    proof: liveProof || selectLatestConnectionProof(null, normalized),
    testPhase: connectionTestPhase || '',
    lastTestedLabel,
    templateVersion,
  });
  const connectionPassed = state.connection.locationVerified;
  const launcherCurrent = state.browser.setupState === QA_LOCAL_SETUP_STATE.CURRENT;
  return {
    ...state,
    providerConfigured: state.provider.configured,
    locationConfigured: state.location.configured,
    connectionPassed,
    connectionReady: Boolean(state.provider.configured && state.location.configured && connectionPassed),
    providerTargetReady: state.location.providerTargetReady,
    providerReady: state.provider.configured,
    targetReady: Boolean(state.location.providerTargetReady && state.location.configured),
    localSetup: {
      state: state.browser.setupState,
      acknowledgedVersion: state.browser.version,
      minimumCompatibleVersion: state.browser.minimumCompatibleVersion,
    },
    localSetupState: state.browser.setupState,
    requiresLauncherUpdate: state.browser.updateRequired,
    canLaunchQaSession: state.launch.eligible,
    sessionLaunchable: state.launch.eligible,
    sessionActive: false,
    launcherCurrent,
    launcherStep: { status: state.steps.launcher.status, detail: state.steps.launcher.detail },
    entitled: state.provider.entitled,
    suspended: state.provider.suspended,
    nextAction: state.primaryAction,
    checklist: [
      { id: 'provider', label: 'Provider configured', done: state.provider.configured },
      { id: 'location', label: 'Location configured', done: state.location.configured },
      { id: 'connection', label: 'Connection verified', done: connectionPassed },
      { id: 'launcher', label: 'QA Browser installed/current', done: launcherCurrent },
    ],
  };
}
